import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { DateInput, Drawer, Field, Select, TextInput } from '../../components/ui/Form';

interface ReturnRow {
  id: string;
  doc_no: string;
  doc_date: string;
  supplier_name: string;
  against_doc_no: string;
  reason: string;
  settlement: string;
  pieces: string;
  grand_total: string;
  status: string;
}

interface Purchase {
  id: string;
  doc_no: string;
  doc_date: string;
  party_name: string;
  purchase_type: string;
}

interface Returnable {
  stock_item_id: string;
  item_code: string;
  status: string;
  fine_wt: string;
  taxable_value: string;
  already_returned: boolean;
}

/**
 * Sending goods back to a supplier, which raises a debit note.
 *
 * The screen shows every piece from the invoice and greys out the ones that
 * cannot go back, with the reason, rather than letting somebody tick a sold
 * bangle and discover on submit that it was never theirs to return.
 */
export function PurchaseReturnsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [chosen, setChosen] = useState<string[]>([]);
  const [form, setForm] = useState({
    purchaseId: '',
    docDate: today(),
    reason: '',
    settlement: 'CREDIT',
    branchId: '',
  });

  const list = useQuery({
    queryKey: ['purchaseReturns'],
    queryFn: () => api.get<{ data: ReturnRow[] }>('/purchase-returns'),
  });
  const purchases = useQuery({
    queryKey: ['purchases'],
    queryFn: () => api.get<{ data: Purchase[] }>('/purchases'),
    enabled: open,
  });
  const returnable = useQuery({
    queryKey: ['returnable', form.purchaseId],
    queryFn: () => api.get<{ data: Returnable[] }>(`/purchases/${form.purchaseId}/returnable`),
    enabled: form.purchaseId !== '',
  });

  // Old gold came from a customer, so there is nobody to send it back to.
  const fromSuppliers = (purchases.data?.data ?? []).filter((p) => p.purchase_type !== 'OLD_GOLD_CUSTOMER');
  const pieces = returnable.data?.data ?? [];

  const create = useMutation({
    mutationFn: () =>
      api.post<{ docNo: string; grandTotal: string }>(
        '/purchase-returns',
        {
          branchId: form.branchId || defaultBranchId,
          docDate: form.docDate,
          purchaseId: form.purchaseId,
          reason: form.reason,
          settlement: form.settlement,
          itemIds: chosen,
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (r) => {
      toast(`Debit note ${r.docNo} raised`, `${money(r.grandTotal)} — stock off the shelf, input tax given back`);
      setOpen(false);
      setChosen([]);
      setForm({ ...form, reason: '', purchaseId: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['purchaseReturns'] });
      void client.invalidateQueries({ queryKey: ['items'] });
      void client.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const toggle = (id: string): void => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const rows = list.data?.data ?? [];
  const chosenValue = pieces
    .filter((p) => chosen.includes(p.stock_item_id))
    .reduce((a, p) => a + Number(p.taxable_value), 0);

  const whyNot = (p: Returnable): string | null => {
    if (p.already_returned) return 'already sent back';
    if (p.status !== 'IN_STOCK') return p.status.replace(/_/g, ' ').toLowerCase();
    return null;
  };

  return (
    <>
      <PageHeader
        title="Purchase returns"
        subtitle="Goods going back to a supplier. Raises a debit note: they owe the shop, in money or against the next invoice."
        actions={
          can('purchase_return.post') ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              Send goods back
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && rows.length === 0 ? (
        <Empty
          message="Nothing sent back to a supplier yet."
          hint="When goods are wrong or faulty, sending them back raises a debit note against the invoice they came in on."
          action={can('purchase_return.post') ? { label: 'Send goods back', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Debit note', 'Supplier', 'Against', 'Why', 'Pieces', 'Settlement', 'Value']}>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="td">{date(r.doc_date)}</td>
              <td className="td num">{r.doc_no}</td>
              <td className="td">{r.supplier_name}</td>
              <td className="td num text-xs">{r.against_doc_no}</td>
              <td className="td text-xs">{r.reason}</td>
              <td className="td num text-right">{r.pieces}</td>
              <td className="td text-xs">
                {r.settlement === 'CREDIT' ? 'against next invoice' : r.settlement.toLowerCase()}
              </td>
              <td className="td num text-right font-medium">{money(r.grand_total)}</td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title="Send goods back"
          subtitle="Choose the invoice they came in on, then the pieces going back."
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label="Against which purchase" required>
              <Select
                value={form.purchaseId}
                onChange={(v) => {
                  setForm({ ...form, purchaseId: v });
                  setChosen([]);
                }}
                options={fromSuppliers.map((p) => ({
                  value: p.id,
                  label: `${p.doc_no} — ${p.party_name} (${date(p.doc_date)})`,
                }))}
                placeholder={fromSuppliers.length === 0 ? 'No supplier purchases yet' : 'Choose…'}
              />
            </Field>

            {form.purchaseId ? (
              <div>
                <p className="label">Pieces going back</p>
                {returnable.isLoading ? <Loading /> : null}
                <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-ink/15 bg-white p-2">
                  {pieces.length === 0 ? (
                    <p className="p-2 text-sm text-ink-soft">Nothing tagged against that invoice.</p>
                  ) : (
                    pieces.map((p) => {
                      const blocked = whyNot(p);
                      return (
                        <label
                          key={p.stock_item_id}
                          className={`flex items-center gap-2 rounded p-1 text-sm ${
                            blocked ? 'opacity-50' : 'hover:bg-gold-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            disabled={blocked !== null}
                            checked={chosen.includes(p.stock_item_id)}
                            onChange={() => toggle(p.stock_item_id)}
                          />
                          <span className="num">{p.item_code}</span>
                          <span className="num text-xs text-ink-soft">{fine(p.fine_wt)}</span>
                          {blocked ? (
                            <span className="ml-auto text-xs text-danger">{blocked}</span>
                          ) : (
                            <span className="num ml-auto text-xs">{money(p.taxable_value)}</span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
                {chosen.length > 0 ? (
                  <p className="mt-2 text-sm">
                    {chosen.length} piece{chosen.length === 1 ? '' : 's'},{' '}
                    <span className="num font-medium">{money(chosenValue.toFixed(2))}</span> before tax
                  </p>
                ) : null}
              </div>
            ) : null}

            <Field
              label="Why they are going back"
              required
              hint="Kept permanently. It is what tells you later which supplier keeps sending the wrong thing."
            >
              <TextInput
                value={form.reason}
                onChange={(v) => setForm({ ...form, reason: v })}
                placeholder="Wrong design — bangles instead of chains"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Date" required>
                <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
              </Field>
              <Field label="How it is settled" required>
                <Select
                  value={form.settlement}
                  onChange={(v) => setForm({ ...form, settlement: v })}
                  options={[
                    { value: 'CREDIT', label: 'Against the next invoice' },
                    { value: 'CASH', label: 'They paid cash back' },
                    { value: 'BANK', label: 'They paid it back by bank' },
                  ]}
                />
              </Field>
            </div>

            {branches.length > 1 ? (
              <Field label="Branch" required>
                <Select
                  value={form.branchId || (defaultBranchId ?? '')}
                  onChange={(v) => setForm({ ...form, branchId: v })}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}

            <button
              className="btn-primary w-full"
              disabled={chosen.length === 0 || form.reason.trim().length < 3 || create.isPending}
            >
              {create.isPending ? 'Raising…' : 'Raise debit note'}
            </button>
          </form>
        </Drawer>
      ) : null}
    </>
  );
}
